/**
 * Selling recommendations at the scale a real catalogue needs.
 *
 * A restaurant with 300 items must never open 300 products to say "offer a
 * drink with a burger". So recommendations live at three levels and resolve in
 * one direction:
 *
 *   product override  →  category rule  →  shop default
 *
 * The merchant-facing language stays plain ("Using category recommendations",
 * "Custom for this item"); the precedence is ours to handle, not theirs.
 */

import { useEffect, useState } from 'react'
import { RecoStyle, addOnsFor, betterOptionsFor } from './sellingRecommendations'

export type TargetType = 'category' | 'product' | 'service'

export interface RuleTarget {
  label: string
  type: TargetType
}

export interface SellingRule {
  better: RuleTarget[]
  addons: RuleTarget[]
  active: boolean
}

export interface ShopStrategy {
  style: RecoStyle
  max: number
  /** Whether Talab may suggest better options / extras at all. */
  better: boolean
  addons: boolean
}

export const catKey = (name: string) => `cat:${name}`
export const itemKey = (id: string) => `item:${id}`

const t = (label: string, type: TargetType): RuleTarget => ({ label, type })

/* ------------------------------------------------------------- suggestions */

export interface Suggestion {
  better: RuleTarget[]
  addons: RuleTarget[]
}

interface TradeSuggestions {
  /** Matched case-insensitively against the merchant's own category names. */
  byCategory?: Record<string, Suggestion>
  /** Used for any category the trade doesn't name explicitly. */
  any?: Suggestion
}

/**
 * What Talab would propose per trade. Keyed on the merchant's own category
 * names where they're predictable, with a sensible fallback for everything
 * else — so an unfamiliar category still gets a useful proposal.
 */
export const TRADE_SUGGESTIONS: Record<string, TradeSuggestions> = {
  'vehicle-rental': {
    byCategory: {
      suvs: {
        better: [t('Luxury', 'category')],
        addons: [t('Child seat', 'service'), t('Airport delivery', 'service')],
      },
      sedans: {
        better: [t('SUVs', 'category')],
        addons: [
          t('Airport delivery', 'service'),
          t('Additional driver', 'service'),
        ],
      },
      economy: {
        better: [t('Sedans', 'category')],
        addons: [t('Full insurance cover', 'service')],
      },
      luxury: {
        better: [],
        addons: [t('Chauffeur service', 'service'), t('Airport delivery', 'service')],
      },
      vans: {
        better: [],
        addons: [t('Additional driver', 'service'), t('Child seat', 'service')],
      },
    },
    any: {
      better: [],
      addons: [t('Airport delivery', 'service'), t('Additional driver', 'service')],
    },
  },
  'vehicle-sale': {
    byCategory: {
      sedans: {
        better: [t('SUVs', 'category')],
        addons: [t('Extended warranty', 'service')],
      },
      suvs: {
        better: [t('Luxury', 'category')],
        addons: [t('Extended warranty', 'service'), t('Service package', 'service')],
      },
      electric: {
        better: [],
        addons: [t('Home charger installation', 'service')],
      },
    },
    any: {
      better: [],
      addons: [t('Extended warranty', 'service'), t('Service package', 'service')],
    },
  },
  restaurant: {
    byCategory: {
      burgers: {
        better: [t('Meals', 'category')],
        addons: [t('Sides', 'category'), t('Drinks', 'category')],
      },
      chicken: {
        better: [t('Meals', 'category')],
        addons: [t('Sides', 'category'), t('Drinks', 'category')],
      },
      'plant-based': {
        better: [t('Meals', 'category')],
        addons: [t('Sides', 'category')],
      },
      sides: {
        better: [],
        addons: [t('Drinks', 'category')],
      },
      breakfast: {
        better: [],
        addons: [t('Drinks', 'category')],
      },
      kids: {
        better: [],
        addons: [t('Desserts', 'category')],
      },
      sharing: {
        better: [],
        addons: [t('Drinks', 'category'), t('Desserts', 'category')],
      },
    },
    any: {
      better: [],
      addons: [t('Drinks', 'category'), t('Sides', 'category')],
    },
  },
  perfume: {
    byCategory: {
      'gift sets': {
        better: [],
        addons: [t('Gift wrapping', 'service')],
      },
    },
    any: {
      better: [t('Larger bottle size', 'product')],
      addons: [t('Gift Sets', 'category'), t('Body lotion', 'product')],
    },
  },
  clothing: {
    byCategory: {
      men: {
        better: [],
        addons: [t('Accessories', 'category'), t('Shoes', 'category')],
      },
      women: {
        better: [],
        addons: [t('Accessories', 'category'), t('Shoes', 'category')],
      },
      shoes: {
        better: [],
        addons: [t('Accessories', 'category')],
      },
    },
    any: {
      better: [t('Premium range', 'product')],
      addons: [t('Accessories', 'category')],
    },
  },
  grocery: {
    byCategory: {
      snacks: {
        better: [t('Family pack', 'product')],
        addons: [t('Beverages', 'category')],
      },
      bakery: {
        better: [],
        addons: [t('Dairy', 'category')],
      },
    },
    any: {
      better: [t('Family pack', 'product')],
      addons: [t('Beverages', 'category')],
    },
  },
  'property-rent': {
    byCategory: {
      apartments: {
        better: [t('Villas', 'category')],
        addons: [t('Furnishing package', 'service')],
      },
      studios: {
        better: [t('Apartments', 'category')],
        addons: [t('Furnishing package', 'service')],
      },
    },
    any: {
      better: [],
      addons: [
        t('Furnishing package', 'service'),
        t('Annual maintenance cover', 'service'),
      ],
    },
  },
  'property-sale': {
    byCategory: {
      apartments: {
        better: [t('Villas', 'category')],
        addons: [t('Mortgage arrangement', 'service')],
      },
    },
    any: {
      better: [],
      addons: [
        t('Mortgage arrangement', 'service'),
        t('Property management', 'service'),
      ],
    },
  },
  electronics: {
    byCategory: {
      phones: {
        better: [],
        addons: [t('Accessories', 'category'), t('Screen protector', 'product')],
      },
      laptops: {
        better: [t('Pro model', 'product')],
        addons: [t('Accessories', 'category')],
      },
    },
    any: {
      better: [],
      addons: [t('Accessories', 'category')],
    },
  },
  service: {
    any: {
      better: [t('Annual maintenance plan', 'product')],
      addons: [t('Priority call-out', 'service')],
    },
  },
}

/** What Talab would propose for one category of one trade. */
export function suggestionFor(
  kind: string,
  category: string,
): Suggestion | null {
  const trade = TRADE_SUGGESTIONS[kind]
  if (!trade) return null
  const named = trade.byCategory?.[category.trim().toLowerCase()]
  const found = named ?? trade.any
  if (!found) return null
  if (found.better.length === 0 && found.addons.length === 0) return null
  return found
}

/**
 * Everything a merchant can point a recommendation at — their own categories
 * first, then the products and services Talab already knows for this trade.
 */
export function targetPool(kind: string, categories: string[]): RuleTarget[] {
  const cats = categories.map((c) => t(c, 'category'))
  const products = betterOptionsFor(kind).map((o) => t(o.name, 'product'))
  const services = addOnsFor(kind).map((o) => t(o.name, 'service'))
  const seen = new Set<string>()
  return [...cats, ...products, ...services].filter((x) => {
    const k = x.label.toLowerCase()
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

/* -------------------------------------------------------------- tiny store */

interface SellingState {
  strategy: ShopStrategy
  rules: Record<string, SellingRule>
}

const DEFAULT_STRATEGY: ShopStrategy = {
  style: 'Balanced',
  max: 2,
  better: true,
  addons: true,
}

let state: SellingState = {
  strategy: DEFAULT_STRATEGY,
  rules: {},
}
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

/** Which trades have already had their starter rules laid down. */
const seeded = new Set<string>()

/**
 * A merchant arriving at the catalogue should already see the shape of this —
 * two or three category rules that make obvious sense for their trade.
 */
export function seedRules(kind: string, categories: string[]) {
  if (seeded.has(kind)) return
  seeded.add(kind)
  const next = { ...state.rules }
  let added = 0
  categories.forEach((c) => {
    if (added >= 2) return
    const s = suggestionFor(kind, c)
    // Only seed where the trade names the category — a generic fallback is a
    // guess, and a wrong guess is worse than an empty row.
    if (!s || !TRADE_SUGGESTIONS[kind]?.byCategory?.[c.toLowerCase()]) return
    next[catKey(c)] = {
      better: s.better,
      addons: s.addons,
      active: true,
    }
    added += 1
  })
  if (added > 0) {
    state = {
      ...state,
      rules: next,
    }
    emit()
  }
}

export function setStrategy(patch: Partial<ShopStrategy>) {
  state = {
    ...state,
    strategy: {
      ...state.strategy,
      ...patch,
    },
  }
  emit()
}

export function saveRule(key: string, rule: SellingRule) {
  state = {
    ...state,
    rules: {
      ...state.rules,
      [key]: rule,
    },
  }
  emit()
}

export function removeRule(key: string) {
  const next = { ...state.rules }
  delete next[key]
  state = {
    ...state,
    rules: next,
  }
  emit()
}

/** Subscribe to the shared selling configuration. */
export function useSelling() {
  const [snapshot, setSnapshot] = useState(state)
  useEffect(() => {
    const l = () => setSnapshot(state)
    listeners.add(l)
    l()
    return () => {
      listeners.delete(l)
    }
  }, [])
  return snapshot
}

export const EMPTY_RULE: SellingRule = {
  better: [],
  addons: [],
  active: true,
}
